import React from 'react';
import { useState, useEffect, useRef } from 'react';
import {convertNumberToWords, formatNumberWithCommas, getAbbreviationForPi} from '../utilities';
import axios from '../axios';
import { useParams } from 'react-router-dom';
import { SalesContractToolbar, emptySalesContract, scDate } from './SalesContractOptions';

const PiPDF = () => {
  const { piId } = useParams();
  const [piInfo, setPiInfo] = useState([]);
  const [companyTitle, setCompanyTitle] = useState(localStorage.getItem('company'));
  const [loading, setLoading] = useState(true);
  const [categoryTotals,setCategoryTotals] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fontScale, setFontScale] = useState(100); // percent, 70–120
  const [rowSpacing, setRowSpacing] = useState(100); // percent, 40–100
  const [pendingSave, setPendingSave] = useState(false);
  const [salesContract, setSalesContract] = useState(emptySalesContract);
  const printableRef = useRef(null);

  // Generic contenteditable sweep — this page has no per-field EditableField
  // wrapper like CommercialPDF's sub-components, so edit mode is applied to
  // whichever leaf-ish elements are reasonable to type into directly. Any
  // element that already carries contenteditable (e.g. after loading a
  // previous edit) is always flipped too, so re-editing keeps working.
  useEffect(() => {
    if (!printableRef.current) return;
    const nodes = printableRef.current.querySelectorAll('td, th, span, strong, h1, h2, div');
    nodes.forEach((el) => {
      if (
        el.hasAttribute('contenteditable') ||
        el.children.length === 0 ||
        el.tagName === 'TD' ||
        el.tagName === 'TH'
      ) {
        el.setAttribute('contenteditable', editMode ? 'true' : 'false');
      }
    });
  }, [editMode, loading]);

  const handleSaveClick = () => {
    if (editMode) {
      setEditMode(false);   // drop into clean view first
      setPendingSave(true); // capture after re-render
    } else {
      captureAndSave();
    }
  };

  const captureAndSave = async () => {
    if (!printableRef.current) return;
    setSaving(true);
    const htmlContent = printableRef.current.outerHTML;
    try {
      await axios.post(`${window.backendUrl}/save_pi_document/${piId}`, { htmlContent });
    } catch (err) {
      console.log(err);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (pendingSave && !editMode) {
      captureAndSave();
      setPendingSave(false);
    }
  }, [editMode, pendingSave]);

  const decreaseFont = () => setFontScale((prev) => Math.max(70, prev - 5));
  const increaseFont = () => setFontScale((prev) => Math.min(120, prev + 5));
  const decreaseSpacing = () => setRowSpacing((prev) => Math.max(40, prev - 10));
  const increaseSpacing = () => setRowSpacing((prev) => Math.min(100, prev + 10));

  useEffect(() => {
    const storedCompany = localStorage.getItem('company');
    if (storedCompany) {
      setCompanyTitle(storedCompany);
    }
  }, []);

  useEffect(() => {
    if (piId) {
      fetch_pi_pdf_info();
    }
  }, [piId]);

  const fetch_pi_pdf_info = () => {
    axios.get(`${window.backendUrl}/get_pi_pdf_info/${piId}`)
      .then((response) => {
        console.log(response.data);
        setPiInfo(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
    // setLoading(false);
  };

  if (loading) {
    return <></>
  }

  // Row spacing (density) control — same mechanism as CommercialPDF's, but this
  // table's th/td pad themselves directly (no inner div layer, no trailing <br>
  // to suppress), so it scales that padding + line-height directly. At 100% no
  // CSS is emitted, so default output is unchanged.
  const spacingFactor = rowSpacing / 100;
  const spacingLineHeight = (1.15 + ((rowSpacing - 40) / 60) * 0.35).toFixed(2);
  const rowSpacingCss =
    rowSpacing < 100
      ? `
        .pi-item-table th, .pi-item-table td {
          padding-top: calc(8px * ${spacingFactor}) !important;
          padding-bottom: calc(8px * ${spacingFactor}) !important;
          line-height: ${spacingLineHeight} !important;
        }
      `
      : '';

  return (
    <>
      <div className="no-print" style={{ margin: '8px 8px 0', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <button onClick={() => setEditMode((prev) => !prev)}>
          {editMode ? 'Normal View' : 'Edit Content'}
        </button>
        <button onClick={handleSaveClick} disabled={saving}>
          {saving ? 'Saving...' : 'Save Document'}
        </button>
        <span style={{ marginLeft: '12px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <button type="button" onClick={decreaseFont} disabled={fontScale <= 70} title="Decrease font size">A−</button>
          <span style={{ minWidth: '48px', textAlign: 'center', fontSize: '13px' }}>{fontScale}%</span>
          <button type="button" onClick={increaseFont} disabled={fontScale >= 120} title="Increase font size">A+</button>
        </span>
        <span style={{ marginLeft: '12px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '13px' }}>Row spacing</span>
          <button type="button" onClick={decreaseSpacing} disabled={rowSpacing <= 40} title="Make table rows more compact">−</button>
          <span style={{ minWidth: '48px', textAlign: 'center', fontSize: '13px' }}>{rowSpacing}%</span>
          <button type="button" onClick={increaseSpacing} disabled={rowSpacing >= 100} title="Make table rows taller">+</button>
        </span>
        <SalesContractToolbar salesContract={salesContract} setSalesContract={setSalesContract} withTerms />
      </div>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          [contenteditable] { outline: none !important; background-color: transparent !important; }
        }
      `}</style>
    <body ref={printableRef} style={{ zoom: fontScale / 100 }}>
    {rowSpacingCss && <style>{rowSpacingCss}</style>}
    <table width="100%" border="0" cellSpacing="5" cellPadding="0" style={{ width: '100%', fontSize:'12px', borderCollapse: 'collapse', marginTop:'15px' }}>
      <tbody>
        <tr>
          <td align="center" valign="top" width="130">
            <img
              style={{ maxHeight: '70px' }}
              src="/Images/miahandsonslogo.png"
              alt="MIAH & SONS INDUSTRIES LTD."
            />
          </td>
          <td align="left" valign="top" nowrap="nowrap">
            <p>
              <strong style={{ fontSize: '22px' }}>{companyTitle}</strong>
              <br />
              <strong style={{ fontSize: '15px', color: '#61190d' }}>Factory Address: </strong>
                {companyTitle === 'MIAH & SONS INDUSTRIES LTD.'
                  ? 'Holding No. 110/1, Barabo, Gazipur-1703, Bangladesh.'
                  : companyTitle === 'Tanvir Printing & Packaging IND.'
                    ? '746/747 NOLVOG, DHORANGERTEK, TURAG, UTTARA, DHAKA-1230'
                    : companyTitle === 'Propack Trims & Accessories Industries'
                      ? 'Muradpur, Shakaswar, Kaliakoir, Gazipur-1703, Bangladesh.'
                      : ''}
              <br />
              <strong style={{ fontSize: '15px', color: '#F36F26' }}>Mobile No.:</strong>{' '}
              <span style={{ color: '#E9590A' }}>{companyTitle === 'Propack Trims & Accessories Industries' ? '+1 (555) 123-4567' : '01711564795, 01866 990700'}</span>
              <strong style={{ fontSize: '15px', color: '#F36F26' }}> Email:</strong>{' '}
              <span style={{ color: '#E9590A' }}>{companyTitle === 'MIAH & SONS INDUSTRIES LTD.' ? 'miahandsons@gmail.com' : companyTitle === 'Tanvir Printing & Packaging IND.' ? 'tanverpack@yahoo.com' : companyTitle === 'Propack Trims & Accessories Industries' ? 'contact@propackusa.com' : ''}</span>
            </p>
          </td>
          <td width="40%" align="left" valign="middle">
            <h2 align="center" style={{ width: '270px',border: '1px solid #333',padding: '8px 20px', marginTop: '0', lineHeight:'18px', fontSize:'20px'}}>
              <strong>{salesContract.on ? 'SALES CONTRACT' : 'PROFORMA INVOICE'}</strong>
            </h2>
            
          </td>    
        </tr>
        
        <tr>
          <td colSpan="3" align="center">
          <hr className='challan_hr' style={{ border: '1px solid black', width: '100%' }} />
          </td>
        </tr>
        <tr>
          <td colSpan="2" valign="top">
            <strong>TO: {piInfo.partyName}</strong>
            <br />
            <div style={{width:'400px',wordWrap: 'break-word', whiteSpace: 'normal' }}>
              <strong>Office Address: </strong>{piInfo.partyAddress}
            </div>
            <div style={{width:'400px',wordWrap: 'break-word', whiteSpace: 'normal' }}>
              <strong>Delivery Address: </strong> {piInfo.delivery_station}
            </div>
            <strong>Mobile No.:</strong>
            <br />
            <strong>Account/Buyer:</strong> {piInfo.buyerName}
            <br />
            <strong>ATTN:</strong> {piInfo.attention}
            <strong> Contact Ref:</strong>
            <br />
          </td>
          <td align="left" valign="top">
            <strong>{salesContract.on ? 'SC No.:' : 'PI No.:'}</strong> {salesContract.on ? 'SC' : 'PI'}/{getAbbreviationForPi(piInfo?.partyName)}/{piInfo?.jobNumber}
            <br />
            <strong>{salesContract.on ? 'SC Date:' : 'PI Date:'}</strong> {new Date(piInfo.createdDate * 1000).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Asia/Dhaka' }).replace(/\//g, '.')}
            <br />
            <br />
            <strong>IRC No.:</strong> 260326120555921
            <br />
            <strong>ERC No.:</strong> 260326210959520
            <br />
            <strong>BIN No.:</strong> 000686131-1205
            <br />
            <strong>TIN No.:</strong> 117556497007
            <br />
          </td>
        </tr>
        <tr>
          <td colSpan="3" align="left" style={{ paddingBottom: '15px' }}>
            This Proforma Invoice (PI) has been issued in relation to the Job No.{' '}
            <strong>{piInfo.jobNumber}</strong> &amp; PO / Style No. <strong>{piInfo.style}</strong> presented by the buyer/customer.
          </td>
        </tr>
        <tr>
          <td colSpan="2" align="left" style={{ paddingBottom: '15px' }}>
            <strong>Generated PI Information</strong>
          </td>
          <td align="right" style={{ paddingBottom: '15px' }}>
            <div
              align="center"
              style={{
                width: '180px',
                border: '1px solid #333',
                padding: '8px 15px',
                marginTop: '0',
              }}
            >
              <strong>H.S. Code: 4819.10.00</strong>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <table className="pi-item-table" width="100%" border="1" cellPadding="0" cellSpacing="0" style={{ borderCollapse: 'collapse' }}>
      <thead style={{fontSize:'12px'}}>
        <tr>
          <th align="center" style={{padding:'8px 10px',backgroundColor: 'rgba(0, 0, 0, 0.05)', border: '1px solid #000' }}>Item Name</th>
          <th align="center" style={{padding:'8px 10px',backgroundColor: 'rgba(0, 0, 0, 0.05)', border: '1px solid #000' }}>Style / PO#</th>
          <th align="center" style={{padding:'8px 10px',backgroundColor: 'rgba(0, 0, 0, 0.05)', border: '1px solid #000' }}>Specification</th>
          <th align="center" style={{padding:'8px 10px',backgroundColor: 'rgba(0, 0, 0, 0.05)', border: '1px solid #000' }}>Measurement</th>
          <th align="center" style={{padding:'8px 10px',backgroundColor: 'rgba(0, 0, 0, 0.05)', border: '1px solid #000' }}>Quantity</th>
          <th align="center" style={{padding:'8px 10px',backgroundColor: 'rgba(0, 0, 0, 0.05)', border: '1px solid #000' }}>Tot. SqrMt/Lbs</th>
          <th align="center" style={{padding:'8px 10px',backgroundColor: 'rgba(0, 0, 0, 0.05)', border: '1px solid #000' }}>Unit Price</th>
          <th align="center" style={{padding:'8px 10px',backgroundColor: 'rgba(0, 0, 0, 0.05)', border: '1px solid #000' }}>Total Amount</th>
        </tr>
      </thead>
      <tbody className='challan_pdf_table' style={{ fontSize: '12px' }}>
        {piInfo.items.map((item) => (
          item.specifications.map((spec) => (
            spec.gsms.map((gsm, gsmIndex) => (
              <React.Fragment key={`${item.itemId}-${spec.specificationId}-${gsm.gsmId}`}>
                {gsmIndex === 0 ? (
                  <tr>
                    <td rowSpan={spec.gsms.length} align="center" style={{ padding: '8px 10px', border: '1px solid #000' }}>
                      {item.itemName}
                    </td>
                    <td rowSpan={spec.gsms.length} align="center" style={{ padding: '8px 10px', border: '1px solid #000' }}>
                      {item.itemStyle}
                    </td>
                    <td rowSpan={spec.gsms.length} align="center" style={{ padding: '8px 10px', border: '1px solid #000' }}>
                      {spec.specification}
                    </td>
                    <td align="center" style={{ padding: '8px 10px', border: '1px solid #000' }}>
                      {gsm.height === 0
                        ? `${gsm.length}x${gsm.width} ${gsm.unit}`
                        : `${gsm.length}x${gsm.width}x${gsm.height} ${gsm.unit}`}
                    </td>
                    <td align="center" style={{ padding: '8px 10px', border: '1px solid #000' }}>
                      {gsm.orderQuantity}/Pcs
                    </td>
                    <td align="center" style={{ padding: '8px 10px', border: '1px solid #000' }}>
                      {(gsm.squareMeter * gsm.orderQuantity).toFixed(3)}
                    </td>
                    <td align="center" style={{ padding: '8px 10px', border: '1px solid #000' }}>
                      ${gsm.unitPrice.toFixed(4)}
                    </td>
                    <td align="center" style={{ padding: '8px 10px', border: '1px solid #000' }}>
                      ${(gsm.unitPrice * gsm.orderQuantity).toFixed(3)}
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td align="center" style={{ padding: '8px 10px', border: '1px solid #000' }}>
                      {gsm.height === 0
                        ? `${gsm.length}x${gsm.width} ${gsm.unit}`
                        : `${gsm.length}x${gsm.width}x${gsm.height} ${gsm.unit}`}
                    </td>
                    <td align="center" style={{ padding: '8px 10px', border: '1px solid #000' }}>
                      {gsm.orderQuantity}/Pcs
                    </td>
                    <td align="center" style={{ padding: '8px 10px', border: '1px solid #000' }}>
                      {(gsm.squareMeter * gsm.orderQuantity).toFixed(3)}
                    </td>
                    <td align="center" style={{ padding: '8px 10px', border: '1px solid #000' }}>
                      ${gsm.unitPrice.toFixed(4)}
                    </td>
                    <td align="center" style={{ padding: '8px 10px', border: '1px solid #000' }}>
                      ${(gsm.unitPrice * gsm.orderQuantity).toFixed(3)}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))
          ))
        ))}

        {/* Calculating Grand Totals */}
        <tr>
          <td colSpan="4" align="right" style={{ padding: '8px 10px', border: '1px solid #000', fontWeight: 'bold' }}>
            Grand Total:
          </td>
          <td align="center" style={{ padding: '8px 10px', border: '1px solid #000', fontWeight: 'bold' }}>
            {piInfo.items.reduce((acc, item) => acc + item.specifications.reduce((specAcc, spec) => 
              specAcc + spec.gsms.reduce((gsmAcc, gsm) => gsmAcc + gsm.orderQuantity, 0), 0), 0)}
          </td>
          <td align="center" style={{ padding: '8px 10px', border: '1px solid #000', fontWeight: 'bold' }}>
            {piInfo.items.reduce((acc, item) => 
              acc + item.specifications.reduce((specAcc, spec) => 
                specAcc + spec.gsms.reduce((gsmAcc, gsm) => 
                  gsmAcc + ((gsm.squareMeter || 0) * (gsm.orderQuantity || 0)), 0), 0), 0).toFixed(3)}
          </td>
          <td style={{ border: '1px solid #000' }}></td>
          <td align="center" style={{ padding: '8px 10px', border: '1px solid #000', fontWeight: 'bold' }}>
            ${formatNumberWithCommas(piInfo.items.reduce((acc, item) => acc + item.specifications.reduce((specAcc, spec) => 
              specAcc + spec.gsms.reduce((gsmAcc, gsm) => gsmAcc + gsm.unitPrice * gsm.orderQuantity, 0), 0), 0).toFixed(2))}
          </td>
        </tr>
      </tbody>


    </table>
{/* /////////////////////////////////////////////////////////////////////////////////////////////// */}
    <div style={{ width: '95%', height: '50px', margin: '15px auto 0', padding: '0', fontSize:'12px'}}>
      <table width="100%" border="1" cellPadding="0" cellSpacing="0" style={{ borderCollapse: 'collapse' }}>
        <tbody>
          <tr>
            <td align="left" className='in_words_pi'>
              <strong>
                In Words (USD): <strong>{convertNumberToWords(piInfo.items.reduce((acc, item) => acc + item.specifications.reduce((specAcc, spec) => 
                  specAcc + spec.gsms.reduce((gsmAcc, gsm) => gsmAcc + gsm.unitPrice * gsm.orderQuantity, 0), 0), 0).toFixed(2))}</strong>
              </strong>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    {salesContract.on ? (
      <SalesContractTerms salesContract={salesContract} piInfo={piInfo} companyTitle={companyTitle} />
    ) : (
    <table border="0" cellSpacing="5" cellPadding="3" style={{ marginLeft: '50px', fontSize:'12px' }}>
      <tbody>
        <tr>
          <td align="left" colSpan="3">
            <br />
            <strong>C. Terms & Condition:</strong>
          </td>
        </tr>
        <tr>
          <td align="left" nowrap="nowrap" width="5%">1. Payment</td>
          <td align="center" width="2%">:</td>
          <td align="left">
            At ({piInfo.credit_days}) Day Sight from the date of shipment by irrevocable letter of credit in USD.
            <br />
            Incorporating your Export L/C number & the Payment must be made at Maturity in USD Draft drawn under asper rules of bangladesh bank
          </td>
        </tr>
        <tr>
          <td align="left" nowrap="nowrap" width="5%">{salesContract.on ? '2.SHIPMENT DATE' : '2.SHIPMENT VALIDITY'}</td>
          <td align="center" width="2%">:</td>
          <td align="left">
          {salesContract.on ? scDate(salesContract.shipmentDate) : '25 (Twenty Five) day from the date of opening L/C'}
          </td>
        </tr>
        <tr>
          <td align="left" nowrap="nowrap" width="5%">3.PRODUCTION</td>
          <td align="center" width="2%">:</td>
          <td align="left">
          Subject to the receiving of L/C.
          </td>
        </tr>
        <tr>
          <td align="left" nowrap="nowrap" width="5%">4.DELIVERY</td>
          <td align="center" width="2%">:</td>
          <td align="left">
            From our factory to your factory site
          </td>
        </tr>
        <tr>
          <td align="left" nowrap="nowrap" width="5%">5.PACKING</td>
          <td align="center" width="2%">:</td>
          <td align="left">
          Standard pack
          </td>
        </tr>
        <tr>
          <td align="left" nowrap="nowrap" width="5%">6.FREIGHT CHARGE</td>
          <td align="center" width="2%">:</td>
          <td align="left">
            Free of Cost
          </td>
        </tr>
        <tr>
          <td align="left" nowrap="nowrap" width="5%">7.INCOTERMS CPT</td>
          <td align="center" width="2%">:</td>
          <td align="left">
            Carriage Paid To (2000 & 2010)
          </td>
        </tr>
        <tr>
          <td align="left" nowrap="nowrap" width="5%">{salesContract.on ? '8.EXPIRY DATE' : '8.PI VALIDITY'}</td>
          <td align="center" width="2%">:</td>
          <td align="left">
          {salesContract.on ? scDate(salesContract.expiryDate) : '35 (THIRTY-FIVE) Days'} {/* From the date of shipment. */}
          </td>
        </tr>
        <tr>
          <td align="left" nowrap="nowrap" width="5%">9.Utilization Declaration</td>
          <td align="center" width="2%">:</td>
          <td align="left">
          Product Details, Qty, Value, BTB L/C No. And Beneficiary's Name Must be Mentioned in the UD.
          </td>
        </tr>
        <tr>
          <td align="left" nowrap="nowrap" width="5%">10.ADVISING BANK</td>
          <td align="center" width="2%">:</td>
          <td align="left">
           <strong>: {companyTitle} : {piInfo.bank_name}</strong>
            <br />
            A/C # {piInfo.bank_account_no}, {piInfo.bank_address}
          </td>
        </tr>
        <tr>
          <td align="left" nowrap="nowrap" width="5%">11.SWIFT Code</td>
          <td align="center" width="2%">:</td>
          <td align="left">
            <strong>{piInfo.bank_swift_code}</strong>
          </td>
        </tr>
        <tr>
          <td align="left" nowrap="nowrap" width="5%">12.Country of Origin</td>
          <td align="center" width="2%">:</td>
          <td align="left">
          Made In Bangladesh
          </td>
        </tr>
        <tr>
          <td align="left" nowrap="nowrap" width="5%">13.Routing No.</td>
          <td align="center" width="2%">:</td>
          <td align="left">
          <strong>{piInfo.bank_routing_no}</strong>
          </td>
        </tr>
        {piInfo.note ? (<tr>
          <td colspan="3" align="left">
            <br/>
            <div style={{ textAlign: 'left', width: '95%', border: '1px solid #333', padding: '8px 15px', marginTop: 0 }}>
              Remarks. {piInfo.note}	
            </div>
          </td>
        </tr>) : null}
      </tbody>
    </table>
    )}


    <table style={{marginTop:'18px'}} align="right" width="100%" border="0" cellSpacing="0" cellPadding="0">
      <tbody>
        <tr>
          <td style={{fontSize:'12px'}} width="50%" align="center">
            <strong>{piInfo.partyName}</strong><br/>
            <div style={{width:'300px', borderTop:'1px solid black', marginTop:'85px'}}>Accepted By</div>
          </td>
          <td width="50%" align="center">
            <strong style={{ fontSize: '15px' }}>{companyTitle}</strong>
            <br />
            <img style={{ maxWidth: '150px' }} src="/Images/miah_and_sons_signature.png" alt="AUTHORISED SIGNATURE" />
            <br />
            <span style={{ borderTop: '1px solid black', fontSize:'12px' }}>AUTHORISED SIGNATURE</span>
          </td>
        </tr>
      </tbody>
    </table>
    </body>
    </>
  );
};

const termRow = (label, value, key) => (
  <tr key={key}>
    <td align="left" nowrap="nowrap" width="5%">{label}</td>
    <td align="center" width="2%">:</td>
    <td align="left" style={{ whiteSpace: 'pre-line' }}>{value}</td>
  </tr>
);

// Terms printed when the PI is turned into a sales contract. Buyer-specific
// lines (others terms, importer bank, description of goods) print only when filled.
const SalesContractTerms = ({ salesContract: sc, piInfo, companyTitle }) => {
  const rows = [
    ['Payment', sc.payment],
    ['SHIPMENT DATE', <strong>{scDate(sc.shipmentDate)}</strong>],
    ['PRODUCTION', 'Subject to the receiving of S/C.'],
    ['DELIVERY', 'From our factory to your factory site'],
    sc.otherTerms.trim() && ['OTHERS TERMS', sc.otherTerms.trim()],
    ['PACKING', 'Standard pack'],
    ['FREIGHT CHARGE', 'Free of Cost'],
    ['INCOTERMS CPT', 'Carriage Paid To (2000 & 2010)'],
    ['EXPIRY DATE', <strong>{scDate(sc.expiryDate)}</strong>],
    ['Utilization Declaration', "Product Details, Qty, Value, S/C No. And Beneficiary's Name Must be Mentioned in the UD/IP."],
    ['ADVISING BANK', <>
      <strong>: {companyTitle} : {piInfo.bank_name}</strong>
      <br />
      A/C # {piInfo.bank_account_no}, {piInfo.bank_address}
    </>],
    ['SWIFT Code', <strong>{piInfo.bank_swift_code}</strong>],
    ['Country of Origin', 'Made In Bangladesh'],
    ['Routing No.', <strong>{piInfo.bank_routing_no}</strong>],
    sc.importerBank.trim() && ['IMPORTER BANK', <strong>{sc.importerBank.trim()}</strong>],
    sc.goodsDescription.trim() && ['Description of Goods', sc.goodsDescription.trim()],
  ].filter(Boolean);
  const net = sc.netWeight.trim();
  const gross = sc.grossWeight.trim();
  return (
    <table border="0" cellSpacing="5" cellPadding="3" style={{ marginLeft: '50px', fontSize:'12px' }}>
      <tbody>
        <tr>
          <td align="left" colSpan="3">
            <br />
            <strong>C. Terms & Condition:</strong>
          </td>
        </tr>
        {rows.map(([label, value], i) => termRow(`${i + 1}.${label}`, value, label))}
        {(net || gross) ? (<tr>
          <td colSpan="3" align="left">
            <br/>
            <div style={{ textAlign: 'left', width: '95%', border: '1px solid #333', padding: '8px 15px', marginTop: 0 }}>
              {net && <strong>Net Weight : {net} Kgs</strong>}
              {net && gross && <>&nbsp;&nbsp;&nbsp;</>}
              {gross && <strong>Gross Weight : {gross} Kgs</strong>}
            </div>
          </td>
        </tr>) : null}
        {piInfo.note ? (<tr>
          <td colSpan="3" align="left">
            <br/>
            <div style={{ textAlign: 'left', width: '95%', border: '1px solid #333', padding: '8px 15px', marginTop: 0 }}>
              Remarks. {piInfo.note}
            </div>
          </td>
        </tr>) : null}
      </tbody>
    </table>
  );
};

export default PiPDF;
